/**
 * Ajustes: perfil, preferencias y exportación (§9: "los datos son del usuario"). Lógica compartida.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  GRUPOS,
  alturaDesdeIMC,
  exportarCSV,
  exportarJSON,
  type ArchivoExportado,
  type GrupoMuscular,
  type Unidad,
} from '@mg/core';
import type { Perfil } from '@mg/sheets';

import { pendientes, sincronizar } from '../datos/sync.ts';
import { guardarPerfil } from './acciones.ts';
import { useDatos } from './useDatos.ts';

export type Tema = 'oscuro' | 'claro';

/**
 * Confirmación efímera JUNTO al control que se tocó.
 *
 * Antes había un "Guardado." global que se pintaba siempre en la tarjeta de Perfil y no se
 * apagaba nunca: no decía qué se había guardado y, cinco minutos después, mentía. La marca
 * dura dos segundos y se limpia sola; el timeout se cancela al desmontar.
 */
export function useMarcaGuardado(ms = 2000) {
  const [marca, setMarca] = useState<string | null>(null);
  const reloj = useRef<number | null>(null);

  const marcar = useCallback(
    (clave: string) => {
      setMarca(clave);
      if (reloj.current !== null) clearTimeout(reloj.current);
      reloj.current = window.setTimeout(() => {
        setMarca(null);
        reloj.current = null;
      }, ms);
    },
    [ms],
  );

  useEffect(
    () => () => {
      if (reloj.current !== null) clearTimeout(reloj.current);
    },
    [],
  );

  return { marca, marcar };
}

/**
 * Un objetivo que escribió el usuario. Vacío significa SIN objetivo, no cero: §10 prohíbe que la
 * app prescriba un número de calorías, así que un campo en blanco no puede convertirse en un 0
 * contra el que después se compare la comida del día.
 */
export function objetivoOpcional(texto: string): number | null {
  const limpio = texto.trim();
  if (limpio === '') return null;
  const n = Number(limpio.replace(',', '.'));
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * De dónde sale la altura que la app propone.
 *
 * El texto vive acá y no en cada pantalla porque las variantes se habían separado y una quedó
 * circular: "De tu IMC sale tu altura" no explica nada a quien no sabe que el IMC ES peso sobre
 * altura al cuadrado. La app dice la cuenta que hizo y quién decide: el usuario.
 */
export function textoAlturaDesdeIMC(cm: number): string {
  return `Tu balanza reporta un IMC; despejando la cuenta (peso ÷ altura²) sale ${cm} cm. Confirmala vos.`;
}

/**
 * Qué es el e1RM. Era el único campo del perfil sin línea de ayuda, y es el más difícil de
 * contestar: elegir entre dos apellidos sin saber qué cambia es adivinar.
 */
export const AYUDA_E1RM =
  'El e1RM estima tu máximo de una repetición a partir de peso × reps; Epley y Brzycki difieren <2 % en el rango 5–15 reps.';

export function useAjustes() {
  const datos = useDatos();
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { marca: guardado, marcar } = useMarcaGuardado();

  const [tema, setTemaEstado] = useState<Tema>(() => {
    try {
      return localStorage.getItem('mg.tema') === 'claro' ? 'claro' : 'oscuro';
    } catch {
      return 'oscuro';
    }
  });

  const setTema = useCallback((t: Tema) => {
    setTemaEstado(t);
    if (t === 'claro') document.documentElement.dataset['tema'] = 'claro';
    else delete document.documentElement.dataset['tema'];
    try {
      localStorage.setItem('mg.tema', t);
    } catch {
      // Sin localStorage el tema vale sólo para esta sesión.
    }
  }, []);

  /** Altura despejada del IMC que ya reportó la balanza (§5.3). */
  const alturaSugerida = useMemo(() => {
    if (datos.perfil?.altura_cm != null) return null;
    const conIMC = [...datos.medidas].reverse().find((m) => m.imc !== null && m.peso > 0);
    return conIMC?.imc ? alturaDesdeIMC(conIMC.peso, conIMC.imc) : null;
  }, [datos.perfil, datos.medidas]);

  /** `control` es la clave del campo que se tocó: es donde se pinta el "✓ Guardado". */
  const guardar = useCallback(
    async (cambios: Partial<Perfil>, control = 'perfil'): Promise<void> => {
      if (!datos.perfil) return;
      setGuardando(true);
      setError(null);
      try {
        await guardarPerfil({ ...datos.perfil, ...cambios });
        marcar(control);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'No se pudo guardar.');
      } finally {
        setGuardando(false);
      }
    },
    [datos.perfil, marcar],
  );

  const setObjetivo = useCallback(
    async (grupo: GrupoMuscular, valor: number | null): Promise<void> => {
      if (!datos.perfil) return;
      const objetivos = { ...datos.perfil.objetivo_series_semanales };
      if (valor === null || valor <= 0) delete objetivos[grupo];
      else objetivos[grupo] = valor;
      await guardar({ objetivo_series_semanales: objetivos }, `grupo:${grupo}`);
    },
    [datos.perfil, guardar],
  );

  // --- Salir sin perder lo que no subió --------------------------------------
  //
  // "Al salir se borran los datos de este dispositivo. Siguen en tu planilla" es falso si hay
  // cambios en la cola: salir llama a `limpiarTodo()` y esos cambios no existen en ningún otro
  // lado. Antes de borrar hay que mirar la cola y decir cuántos son.

  const [salida, setSalida] = useState<{ pendientes: number } | null>(null);
  const [subiendo, setSubiendo] = useState(false);
  const [errorSalida, setErrorSalida] = useState<string | null>(null);

  const intentarSalir = useCallback(async (salir: () => Promise<void>): Promise<void> => {
    setErrorSalida(null);
    const cuantos = await pendientes();
    if (cuantos === 0) {
      await salir();
      return;
    }
    setSalida({ pendientes: cuantos });
  }, []);

  const subirYSalir = useCallback(async (salir: () => Promise<void>): Promise<void> => {
    setSubiendo(true);
    setErrorSalida(null);
    try {
      await sincronizar();
      const quedan = await pendientes();
      if (quedan === 0) {
        await salir();
        return;
      }
      // Lo que queda es lo que el servidor rechazó: subir de nuevo no lo va a arreglar.
      setSalida({ pendientes: quedan });
      setErrorSalida(
        `Subió todo menos ${quedan} ${quedan === 1 ? 'cambio' : 'cambios'}: el servidor los rechazó. El motivo está en la sección Sincronización, en esta misma pantalla.`,
      );
    } catch (e) {
      setErrorSalida(e instanceof Error ? e.message : 'No se pudo subir.');
    } finally {
      setSubiendo(false);
    }
  }, []);

  const cancelarSalida = useCallback(() => {
    setSalida(null);
    setErrorSalida(null);
  }, []);

  /** Los datos completos, listos para exportar. */
  const paquete = useMemo(() => {
    if (!datos.perfil) return null;
    return {
      usuario: {
        id: datos.perfil.id,
        nombre: datos.perfil.nombre,
        fecha_nacimiento: datos.perfil.fecha_nacimiento,
        sexo: datos.perfil.sexo,
        altura_cm: datos.perfil.altura_cm,
        unidad_preferida: datos.perfil.unidad_preferida,
        zona_horaria: datos.perfil.zona_horaria,
      },
      ejercicios: datos.ejercicios,
      maquinas: datos.maquinas,
      planes: datos.planes,
      diasBloqueados: datos.diasBloqueados,
      rutinas: datos.rutinas,
      rutinaDias: datos.rutinaDias,
      rutinaEjercicios: datos.rutinaEjercicios,
      sesiones: datos.sesiones,
      series: datos.series,
      cardio: datos.cardio,
      medidas: datos.medidas,
      ingestas: datos.ingestas,
    };
  }, [datos]);

  const descargar = useCallback((archivo: ArchivoExportado) => {
    const blob = new Blob([archivo.contenido], { type: `${archivo.mime};charset=utf-8` });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = archivo.nombre;
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  const exportarTodoJSON = useCallback(() => {
    if (!paquete) return;
    const archivo = exportarJSON(paquete);
    descargar({
      ...archivo,
      nombre: `mg-${datos.hoy}.json`,
      contenido: JSON.stringify({ version: 1, exportado_en: new Date().toISOString(), ...paquete }, null, 2),
    });
  }, [paquete, descargar, datos.hoy]);

  const archivosCSV = useMemo<ArchivoExportado[]>(() => (paquete ? exportarCSV(paquete) : []), [paquete]);

  return {
    datos,
    guardar,
    guardando,
    /** Clave del control que acaba de guardarse; se apaga sola a los dos segundos. */
    guardado,
    error,
    tema,
    setTema,
    alturaSugerida,
    setObjetivo,
    grupos: GRUPOS,
    salida,
    subiendo,
    errorSalida,
    intentarSalir,
    subirYSalir,
    cancelarSalida,
    exportarTodoJSON,
    archivosCSV,
    descargar,
    unidades: ['kg', 'lb'] as Unidad[],
  };
}
